import { ref, isRef, Ref } from 'vue'
import { useCommand } from '@cqrs/vue-composables-cqrs'
import * as c from './commands-core'

export function useAcceptWorkspaceInvitationCommandV1(
	id?: string | Ref<string> | (() => string), 
	recipientEmail?: string | Ref<string> | (() => string), 
) 
{
	const _id = isRef<string>(id) ? id : ref<string>((typeof id === 'function') ? id() : id)
	const _recipientEmail = isRef<string>(recipientEmail) ? recipientEmail : ref<string>((typeof recipientEmail === 'function') ? recipientEmail() : recipientEmail)

	const result = {
		id: _id,
		recipientEmail: _recipientEmail,

		...useCommand(() => {
			if (typeof id === 'function') { _id.value = id() }
			if (typeof recipientEmail === 'function') { _recipientEmail.value = recipientEmail() }

			return new c.AcceptWorkspaceInvitationCommandV1(_id.value, _recipientEmail.value)
		})
	}

	return {
		...result,
		authorizedAcceptWorkspaceInvitationCommandV1: result.authorized,
		acceptWorkspaceInvitationCommandV1: result.command,
		completedAcceptWorkspaceInvitationCommandV1: result.completed,
		errorAcceptWorkspaceInvitationCommandV1: result.error,
		invokeAcceptWorkspaceInvitationCommandV1: result.invoke,
		invokingAcceptWorkspaceInvitationCommandV1: result.invoking,
		succeedAcceptWorkspaceInvitationCommandV1: result.succeed,
		validationErrorsAcceptWorkspaceInvitationCommandV1: result.validationErrors,
		onSucceedAcceptWorkspaceInvitationCommandV1: result.onSucceed,
	}
}

export function useAddUserToWorkspaceCommandV1(
	id?: string | Ref<string> | (() => string), 
	userId?: string | Ref<string> | (() => string), 
) 
{
	const _id = isRef<string>(id) ? id : ref<string>((typeof id === 'function') ? id() : id)
	const _userId = isRef<string>(userId) ? userId : ref<string>((typeof userId === 'function') ? userId() : userId)

	const result = {
		id: _id,
		userId: _userId,

		...useCommand(() => {
			if (typeof id === 'function') { _id.value = id() }
			if (typeof userId === 'function') { _userId.value = userId() }

			return new c.AddUserToWorkspaceCommandV1(_id.value, _userId.value)
		})
	}

	return {
		...result,
		authorizedAddUserToWorkspaceCommandV1: result.authorized,
		addUserToWorkspaceCommandV1: result.command,
		completedAddUserToWorkspaceCommandV1: result.completed,
		errorAddUserToWorkspaceCommandV1: result.error,
		invokeAddUserToWorkspaceCommandV1: result.invoke,
		invokingAddUserToWorkspaceCommandV1: result.invoking,
		succeedAddUserToWorkspaceCommandV1: result.succeed,
		validationErrorsAddUserToWorkspaceCommandV1: result.validationErrors,
		onSucceedAddUserToWorkspaceCommandV1: result.onSucceed,
	}
}

export function useCreateWorkspaceCommandV1(
	id?: string | Ref<string> | (() => string), 
	organizationUnitId?: number | Ref<number> | (() => number), 
	name?: string | Ref<string> | (() => string), 
	description?: string | Ref<string> | (() => string), 
	startupUrl?: string | Ref<string> | (() => string), 
) 
{
	const _id = isRef<string>(id) ? id : ref<string>((typeof id === 'function') ? id() : id)
	const _organizationUnitId = isRef<number>(organizationUnitId) ? organizationUnitId : ref<number>((typeof organizationUnitId === 'function') ? organizationUnitId() : organizationUnitId)
	const _name = isRef<string>(name) ? name : ref<string>((typeof name === 'function') ? name() : name)
	const _description = isRef<string>(description) ? description : ref<string>((typeof description === 'function') ? description() : description)
	const _startupUrl = isRef<string>(startupUrl) ? startupUrl : ref<string>((typeof startupUrl === 'function') ? startupUrl() : startupUrl)

	const result = {
		id: _id,
		organizationUnitId: _organizationUnitId,
		name: _name,
		description: _description,
		startupUrl: _startupUrl,

		...useCommand(() => {
			if (typeof id === 'function') { _id.value = id() }
			if (typeof organizationUnitId === 'function') { _organizationUnitId.value = organizationUnitId() }
			if (typeof name === 'function') { _name.value = name() }
			if (typeof description === 'function') { _description.value = description() }
			if (typeof startupUrl === 'function') { _startupUrl.value = startupUrl() }

			return new c.CreateWorkspaceCommandV1(_id.value, _organizationUnitId.value, _name.value, _description.value, _startupUrl.value)
		})
	}

	return {
		...result,
		authorizedCreateWorkspaceCommandV1: result.authorized,
		createWorkspaceCommandV1: result.command,
		completedCreateWorkspaceCommandV1: result.completed,
		errorCreateWorkspaceCommandV1: result.error,
		invokeCreateWorkspaceCommandV1: result.invoke,
		invokingCreateWorkspaceCommandV1: result.invoking,
		succeedCreateWorkspaceCommandV1: result.succeed,
		validationErrorsCreateWorkspaceCommandV1: result.validationErrors,
		onSucceedCreateWorkspaceCommandV1: result.onSucceed,
	}
}

export function useDeleteWorkspaceCommandV1(
	id?: string | Ref<string> | (() => string), 
) 
{
	const _id = isRef<string>(id) ? id : ref<string>((typeof id === 'function') ? id() : id)

	const result = {
		id: _id,

		...useCommand(() => {
			if (typeof id === 'function') { _id.value = id() }

			return new c.DeleteWorkspaceCommandV1(_id.value)
		})
	}

	return {
		...result,
		authorizedDeleteWorkspaceCommandV1: result.authorized,
		deleteWorkspaceCommandV1: result.command,
		completedDeleteWorkspaceCommandV1: result.completed,
		errorDeleteWorkspaceCommandV1: result.error,
		invokeDeleteWorkspaceCommandV1: result.invoke,
		invokingDeleteWorkspaceCommandV1: result.invoking,
		succeedDeleteWorkspaceCommandV1: result.succeed,
		validationErrorsDeleteWorkspaceCommandV1: result.validationErrors,
		onSucceedDeleteWorkspaceCommandV1: result.onSucceed,
	}
}

export function useImportToWorkspaceCommandV1(
	id?: string | Ref<string> | (() => string), 
	blobId?: string | Ref<string> | (() => string), 
) 
{
	const _id = isRef<string>(id) ? id : ref<string>((typeof id === 'function') ? id() : id)
	const _blobId = isRef<string>(blobId) ? blobId : ref<string>((typeof blobId === 'function') ? blobId() : blobId)

	const result = {
		id: _id,
		blobId: _blobId,

		...useCommand(() => {
			if (typeof id === 'function') { _id.value = id() }
			if (typeof blobId === 'function') { _blobId.value = blobId() }

			return new c.ImportToWorkspaceCommandV1(_id.value, _blobId.value)
		})
	}

	return {
		...result,
		authorizedImportToWorkspaceCommandV1: result.authorized,
		importToWorkspaceCommandV1: result.command,
		completedImportToWorkspaceCommandV1: result.completed,
		errorImportToWorkspaceCommandV1: result.error,
		invokeImportToWorkspaceCommandV1: result.invoke,
		invokingImportToWorkspaceCommandV1: result.invoking,
		succeedImportToWorkspaceCommandV1: result.succeed,
		validationErrorsImportToWorkspaceCommandV1: result.validationErrors,
		onSucceedImportToWorkspaceCommandV1: result.onSucceed,
	}
}

export function useInviteToWorkspaceCommandV1(
	id?: string | Ref<string> | (() => string), 
	recipientEmail?: string | Ref<string> | (() => string), 
) 
{
	const _id = isRef<string>(id) ? id : ref<string>((typeof id === 'function') ? id() : id)
	const _recipientEmail = isRef<string>(recipientEmail) ? recipientEmail : ref<string>((typeof recipientEmail === 'function') ? recipientEmail() : recipientEmail)

	const result = {
		id: _id,
		recipientEmail: _recipientEmail,

		...useCommand(() => {
			if (typeof id === 'function') { _id.value = id() }
			if (typeof recipientEmail === 'function') { _recipientEmail.value = recipientEmail() }

			return new c.InviteToWorkspaceCommandV1(_id.value, _recipientEmail.value)
		})
	}

	return {
		...result,
		authorizedInviteToWorkspaceCommandV1: result.authorized,
		inviteToWorkspaceCommandV1: result.command,
		completedInviteToWorkspaceCommandV1: result.completed,
		errorInviteToWorkspaceCommandV1: result.error,
		invokeInviteToWorkspaceCommandV1: result.invoke,
		invokingInviteToWorkspaceCommandV1: result.invoking,
		succeedInviteToWorkspaceCommandV1: result.succeed,
		validationErrorsInviteToWorkspaceCommandV1: result.validationErrors,
		onSucceedInviteToWorkspaceCommandV1: result.onSucceed,
	}
}

export function useRejectWorkspaceInvitationCommandV1(
	id?: string | Ref<string> | (() => string), 
	recipientEmail?: string | Ref<string> | (() => string), 
) 
{
	const _id = isRef<string>(id) ? id : ref<string>((typeof id === 'function') ? id() : id)
	const _recipientEmail = isRef<string>(recipientEmail) ? recipientEmail : ref<string>((typeof recipientEmail === 'function') ? recipientEmail() : recipientEmail)

	const result = {
		id: _id,
		recipientEmail: _recipientEmail,

		...useCommand(() => {
			if (typeof id === 'function') { _id.value = id() }
			if (typeof recipientEmail === 'function') { _recipientEmail.value = recipientEmail() }

			return new c.RejectWorkspaceInvitationCommandV1(_id.value, _recipientEmail.value)
		})
	}

	return {
		...result,
		authorizedRejectWorkspaceInvitationCommandV1: result.authorized,
		rejectWorkspaceInvitationCommandV1: result.command,
		completedRejectWorkspaceInvitationCommandV1: result.completed,
		errorRejectWorkspaceInvitationCommandV1: result.error,
		invokeRejectWorkspaceInvitationCommandV1: result.invoke,
		invokingRejectWorkspaceInvitationCommandV1: result.invoking,
		succeedRejectWorkspaceInvitationCommandV1: result.succeed,
		validationErrorsRejectWorkspaceInvitationCommandV1: result.validationErrors,
		onSucceedRejectWorkspaceInvitationCommandV1: result.onSucceed,
	}
}

export function useRemoveUserFromWorkspaceCommandV1(
	id?: string | Ref<string> | (() => string), 
	userId?: string | Ref<string> | (() => string), 
) 
{
	const _id = isRef<string>(id) ? id : ref<string>((typeof id === 'function') ? id() : id)
	const _userId = isRef<string>(userId) ? userId : ref<string>((typeof userId === 'function') ? userId() : userId)

	const result = {
		id: _id,
		userId: _userId,

		...useCommand(() => {
			if (typeof id === 'function') { _id.value = id() }
			if (typeof userId === 'function') { _userId.value = userId() }

			return new c.RemoveUserFromWorkspaceCommandV1(_id.value, _userId.value)
		})
	}

	return {
		...result,
		authorizedRemoveUserFromWorkspaceCommandV1: result.authorized,
		removeUserFromWorkspaceCommandV1: result.command,
		completedRemoveUserFromWorkspaceCommandV1: result.completed,
		errorRemoveUserFromWorkspaceCommandV1: result.error,
		invokeRemoveUserFromWorkspaceCommandV1: result.invoke,
		invokingRemoveUserFromWorkspaceCommandV1: result.invoking,
		succeedRemoveUserFromWorkspaceCommandV1: result.succeed,
		validationErrorsRemoveUserFromWorkspaceCommandV1: result.validationErrors,
		onSucceedRemoveUserFromWorkspaceCommandV1: result.onSucceed,
	}
}

export function useUpdateWorkspaceCommandV1(
	id?: string | Ref<string> | (() => string), 
	name?: string | Ref<string> | (() => string), 
	description?: string | Ref<string> | (() => string), 
	startupUrl?: string | Ref<string> | (() => string), 
) 
{
	const _id = isRef<string>(id) ? id : ref<string>((typeof id === 'function') ? id() : id)
	const _name = isRef<string>(name) ? name : ref<string>((typeof name === 'function') ? name() : name)
	const _description = isRef<string>(description) ? description : ref<string>((typeof description === 'function') ? description() : description)
	const _startupUrl = isRef<string>(startupUrl) ? startupUrl : ref<string>((typeof startupUrl === 'function') ? startupUrl() : startupUrl)

	const result = {
		id: _id,
		name: _name,
		description: _description,
		startupUrl: _startupUrl,

		...useCommand(() => {
			if (typeof id === 'function') { _id.value = id() }
			if (typeof name === 'function') { _name.value = name() }
			if (typeof description === 'function') { _description.value = description() }
			if (typeof startupUrl === 'function') { _startupUrl.value = startupUrl() }

			return new c.UpdateWorkspaceCommandV1(_id.value, _name.value, _description.value, _startupUrl.value)
		})
	}

	return {
		...result,
		authorizedUpdateWorkspaceCommandV1: result.authorized,
		updateWorkspaceCommandV1: result.command,
		completedUpdateWorkspaceCommandV1: result.completed,
		errorUpdateWorkspaceCommandV1: result.error,
		invokeUpdateWorkspaceCommandV1: result.invoke,
		invokingUpdateWorkspaceCommandV1: result.invoking,
		succeedUpdateWorkspaceCommandV1: result.succeed,
		validationErrorsUpdateWorkspaceCommandV1: result.validationErrors,
		onSucceedUpdateWorkspaceCommandV1: result.onSucceed,
	}
}

